import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getSessionToken, wsOrigin } from "@/api/client";
import { MEETING_KEYS } from "./use-meetings";

const RECONNECT_DELAY_MS = 3_000;

/**
 * Subscribe to the user's lifecycle channel for SESSION_CREATED / SESSION_ENDED
 * pushes. Closes the gap where the desktop creates a session and the web tab
 * has nothing to subscribe to until the next poll lands. On every push we
 * invalidate the meetings list query so React-Query refetches and the active
 * card appears within ~300 ms of the click.
 *
 * Reconnect/auth shape mirrors meetings/ws/transcript-socket.ts; this is
 * intentionally a small standalone implementation rather than a base class
 * factor-out, since the hook lifecycle (mount/unmount) maps cleanly to
 * connect/disconnect and there's no shared state beyond reconnect timing.
 */
export function useUserSocket(userId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      const token = getSessionToken();
      if (!token) {
        // No token yet — try again in a moment. The auth provider sets the
        // token before the user renders, but a hard refresh during sign-in
        // can land us here briefly with no token in storage.
        reconnectTimer = setTimeout(open, RECONNECT_DELAY_MS);
        return;
      }
      // Connect straight to the backend WS origin (VITE_WS_BASE_URL), NOT
      // window.location.host — the web-origin proxy drops root /ws/* on
      // staging/prod, so a same-origin socket never delivers SESSION_ENDED and
      // the meetings list stays stale. See wsOrigin() for the full rationale.
      const url = `${wsOrigin()}/ws/user/${encodeURIComponent(
        userId,
      )}?session=${encodeURIComponent(token)}`;
      ws = new WebSocket(url);

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type === "SESSION_CREATED" || msg.type === "SESSION_ENDED") {
            // Cheapest correct thing: invalidate the meetings list. React-Query
            // refetches and renders the new/updated card. Avoids hand-merging
            // partial payloads into cached state.
            queryClient.invalidateQueries({ queryKey: MEETING_KEYS.list() });
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onclose = (event) => {
        ws = null;
        if (cancelled) return;
        // 4001 = unauthorized. Don't loop on a bad token; let auth refresh
        // trigger a remount of this hook with a fresh token.
        if (event.code === 4001) return;
        reconnectTimer = setTimeout(open, RECONNECT_DELAY_MS);
      };

      ws.onerror = () => {
        // onclose will follow; reconnect logic lives there.
      };
    };

    open();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null;
        ws.close();
        ws = null;
      }
    };
  }, [userId, queryClient]);
}
